import { useEffect, useState } from "react";
import { rtctApi } from "services/rtctApi";
import { localText } from "utils/overpasses";
import { passText, readInsar, waterText } from "utils/insar";
import { useStableState } from "utils/stableState";

// InSAR ground truth on the node page (GET devices/<mac>/insar/): the Sentinel-1 acquisitions over the node in the
// last 60 days, each with the field's water when the radar imaged it, and ESA's planned ones ahead; the whole table as
// CSV. The acquisitions come from the Copernicus catalogue, asked by the 1-degree box around the node (nothing nearer
// leaves the server), and appear there a few hours after the pass. Asked for every hour.
const DAYS = 60;
const SHOWN = 8;
const POLL_MS = 60 * 60 * 1000;

export default function InsarGroundTruth({ mac }) {
  const [state, setState] = useStableState({ mac: "", data: null, error: "" });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  useEffect(() => {
    if (!mac || typeof rtctApi.insar !== "function") return undefined;
    let abort = new AbortController();
    const load = () => {
      abort.abort();
      abort = new AbortController();
      const signal = abort.signal;
      Promise.resolve(rtctApi.insar(mac, signal, DAYS))
        .then((data) => {
          if (!signal.aborted) setState({ mac, data: readInsar(data), error: "" });
        })
        .catch((e) => {
          if (!signal.aborted) setState((old) => ({ ...old, mac, error: e?.message || "InSAR ground truth unavailable" }));
        });
    };
    load();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    return () => {
      clearInterval(timer);
      abort.abort();
    };
  }, [mac, setState]);
  const data = state.mac === mac ? state.data : null;
  if (!data && !(state.mac === mac && state.error)) return null;
  const rows = data?.rows || [];
  const download = async () => {
    setBusy(true);
    setProblem("");
    try {
      await rtctApi.downloadInsar(mac, DAYS);
    } catch (e) {
      setProblem(e?.message || "The table could not be downloaded");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="field-panel" aria-label="InSAR ground truth">
      <div className="field-panel-head">
        <div>
          <h2>InSAR ground truth</h2>
          <p>Sentinel-1 over this node, last {data?.days || DAYS} days</p>
        </div>
        <span className="field-state">{rows.filter((r) => r.status === "acquired").length} acquired</span>
      </div>
      <div className="field-panel-body">
        {state.error && <div className="field-alert">{state.error}</div>}
        {data?.problem && <div className="field-notice">{data.problem}.</div>}
        {data?.catalogueError && (
          <div className="field-notice">The catalogue could not be reached ({data.catalogueError}); showing its last answer.</div>
        )}
        {rows.length > 0 && (
          <div className="field-table-scroll">
            <table className="field-table" aria-label="Sentinel-1 acquisitions">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Satellite</th>
                  <th>Pass</th>
                  <th>Water</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, SHOWN).map((r) => (
                  <tr key={`${r.satellite}-${r.t}`}>
                    <td>{localText(r.local)}</td>
                    <td>{r.satellite}</td>
                    <td>{passText(r)}</td>
                    <td>{waterText(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && !data.problem && rows.length === 0 && (
          <div className="field-notice">No Sentinel-1 acquisition over this node in the last {data.days} days.</div>
        )}
        <p className="field-caption">
          Acquired: the Copernicus catalogue's Sentinel-1 radar images that cover the node, a few hours after each pass.
          Water: the node's own reading nearest that minute. Planned: ESA's acquisition plan.
        </p>
        <div className="field-actions">
          <button type="button" className="field-button" disabled={busy || !rows.length} onClick={download}>
            {busy ? "Downloading…" : "Download as CSV"}
          </button>
        </div>
        {problem && <div className="field-alert">{problem}</div>}
      </div>
    </section>
  );
}
